import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft, Play } from "lucide-react";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

export default function ProgramDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planId, setPlanId] = useState(null);

  useEffect(() => {
    api.get(`/programs/${id}`).then((r) => setProgram(r.data)).catch(() => setProgram(null)).finally(() => setLoading(false));
  }, [id]);

  const isSubscription = program && (program.interval === "month" || program.interval === "year");

  useEffect(() => {
    if (program && user && isSubscription) {
      api.post("/paypal/ensure-plan", { program_id: program.id })
        .then((r) => setPlanId(r.data.plan_id)).catch(() => {});
    }
  }, [program, user, isSubscription]);

  const createOrder = async () => {
    const { data } = await api.post("/paypal/create-order", { program_id: program.id });
    return data.order_id;
  };

  const onApprove = async (data) => {
    try {
      const res = await api.post(`/paypal/capture-order/${data.orderID}`);
      if (res.data.payment_status === "paid") {
        toast.success("Payment complete! Your program is active.");
        nav("/payment/success?provider=paypal");
      } else {
        toast.error("Payment could not be completed.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Payment capture failed");
    }
  };

  const onApproveSubscription = async (data) => {
    try {
      await api.post("/paypal/record-subscription", { subscription_id: data.subscriptionID, program_id: program.id });
      toast.success("Subscription active! Welcome aboard.");
      nav("/payment/success?provider=paypal");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not activate subscription");
    }
  };

  if (loading) return <div className="min-h-screen bg-soma-bg flex items-center justify-center text-soma-muted">Loading…</div>;
  if (!program) return <div className="min-h-screen bg-soma-bg flex items-center justify-center text-soma-muted">Program not found.</div>;

  return (
    <div className="relative bg-soma-bg min-h-screen">
      <Nav />
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-32 pb-32">
        <button onClick={() => nav("/programs")} data-testid="back-to-programs"
          className="flex items-center gap-2 text-soma-muted hover:text-white transition-colors mb-12">
          <ArrowLeft className="w-4 h-4" /> All programs
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Image — spotlight, clipped */}
          <motion.div initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
            className="lg:col-span-6">
            <div className="relative overflow-hidden border border-soma-border aspect-[4/5] bg-soma-surface">
              <img src={program.image} alt={program.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-soma-bg/60 to-transparent" />
              <div className="absolute top-6 left-6 text-[11px] uppercase tracking-[0.2em] px-3 py-1 border border-white/20 bg-black/40 backdrop-blur-sm">
                {program.level} · {program.duration}
              </div>
            </div>
          </motion.div>

          {/* Details */}
          <div className="lg:col-span-6 lg:pt-4">
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="font-serif-editorial italic text-xl mb-4" style={{ color: program.accent }}>{program.tagline}</motion.p>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="font-display font-semibold text-4xl lg:text-5xl leading-[0.95] tracking-tight mb-8">{program.name}</motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="text-soma-muted text-lg leading-relaxed mb-10">{program.description}</motion.p>

            <div className="border-t border-soma-border pt-8 mb-10">
              <h3 className="text-xs uppercase tracking-[0.3em] text-soma-muted mb-6">What's included</h3>
              <ul className="space-y-4">
                {program.features?.map((f, i) => (
                  <motion.li key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 + i * 0.06 }}
                    className="flex items-start gap-3">
                    <span className="mt-1 w-5 h-5 rounded-full bg-soma-primary/15 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-soma-primary" strokeWidth={3} />
                    </span>
                    <span className="text-white">{f}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col gap-6">
              <div>
                <div className="font-display font-bold text-5xl text-white">
                  ${program.price}{isSubscription && <span className="text-soma-muted text-xl font-normal">/{program.interval === "year" ? "yr" : "mo"}</span>}
                </div>
                <p className="text-xs text-soma-muted mt-1">
                  {isSubscription ? "Recurring via PayPal — cancel anytime" : "Secure one-time payment via PayPal"}
                </p>
              </div>
              {user ? (
                <div className="w-full sm:max-w-sm" data-testid="paypal-checkout">
                  {isSubscription ? (
                    <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "USD", intent: "subscription", vault: true }}>
                      <PayPalButtons
                        style={{ layout: "vertical", color: "gold", shape: "pill", label: "subscribe" }}
                        disabled={!planId}
                        createSubscription={(data, actions) => actions.subscription.create({ plan_id: planId })}
                        onApprove={onApproveSubscription}
                        onError={() => toast.error("PayPal error. Please try again.")}
                      />
                    </PayPalScriptProvider>
                  ) : (
                    <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "USD", intent: "capture" }}>
                      <PayPalButtons
                        style={{ layout: "vertical", color: "gold", shape: "pill", label: "paypal" }}
                        createOrder={createOrder}
                        onApprove={onApprove}
                        onError={() => toast.error("PayPal error. Please try again.")}
                      />
                    </PayPalScriptProvider>
                  )}
                </div>
              ) : (
                <button onClick={() => nav("/login")} data-testid="login-to-pay"
                  className="self-start px-10 py-4 rounded-full bg-soma-primary text-soma-bg font-semibold hover:bg-soma-primaryHover transition-colors duration-300 cyan-glow">
                  Log in to subscribe
                </button>
              )}
            </div>

            {user && (
              <button onClick={() => nav(`/player/${program.id}`)} data-testid="try-session"
                className="mt-6 flex items-center gap-2 text-sm text-soma-muted hover:text-soma-primary transition-colors">
                <Play className="w-3.5 h-3.5" fill="currentColor" /> Try a free focus session first
              </button>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
